import { Menu } from "@mantine/core";
import {
  IconAward,
  IconBug,
  IconBuilding,
  IconBuildingStore,
  IconCar,
  IconEdit,
  IconFileInvoice,
  IconFrame,
  IconGraph,
  IconGrid4x4,
  IconHeartHandshake,
  IconHistoryToggle,
  IconMap,
  IconSchool,
  IconStopwatch,
  IconSteeringWheel,
  IconTrophy,
  IconUsers,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { ReactNode } from "react";
import useHasPermission from "../../../../../../../../../api/useHasPermission";
import {
  ADMIN_ACADEMY_PATH,
  ADMIN_ALL_SIMULATORS_PATH,
  ADMIN_ALL_USERS_PATH,
  ADMIN_COMPETITIONS_PATH,
  ADMIN_COURSE_EDITOR_STATS_PATH,
  ADMIN_EXERCISE_LEADERBOARD_PATH,
  ADMIN_MAP_PATH,
  ADMIN_ORGANIZATIONS_PATH,
  ADMIN_RUNDATA_PATH,
  ADMIN_SCHOOLS_PATH,
  ADMIN_STATS_PATH,
  ADMIN_TOPLIST_PATH,
} from "../../../../../../../../../routes";

export const useAdminMenuItems = (): {
  label: string;
  icon: ReactNode;
  to: string;
  groupIndex: number;
}[] => {
  const hasBillingPermission = useHasPermission({
    permissionCode: "billing:readwrite",
  });

  const iconProps = { size: 16, strokeWidth: 1.5 };
  const items = [
    {
      label: "Organizations",
      icon: <IconBuilding {...iconProps} />,
      to: ADMIN_ORGANIZATIONS_PATH,
      groupIndex: 0,
    },
    {
      label: "Schools",
      icon: <IconBuildingStore {...iconProps} />,
      to: ADMIN_SCHOOLS_PATH,
      groupIndex: 0,
    },
    {
      label: "Users",
      icon: <IconUsers {...iconProps} />,
      to: ADMIN_ALL_USERS_PATH,
      groupIndex: 0,
    },
    {
      label: "Simulators",
      icon: <IconSteeringWheel {...iconProps} />,
      to: ADMIN_ALL_SIMULATORS_PATH,
      groupIndex: 0,
    },
    {
      label: "Tickets",
      icon: <IconBug {...iconProps} />,
      to: "/admin/tickets",
      groupIndex: 0,
    },
    {
      label: "Stats",
      icon: <IconGraph {...iconProps} />,
      to: ADMIN_STATS_PATH,
      groupIndex: 1,
    },
    {
      label: "Framerates",
      icon: <IconFrame {...iconProps} />,
      to: "/admin/framerates",
      groupIndex: 1,
    },
    {
      label: "Run data",
      icon: <IconCar {...iconProps} />,
      to: ADMIN_RUNDATA_PATH,
      groupIndex: 1,
    },
    {
      label: "Exercise leaderboard",
      icon: <IconStopwatch {...iconProps} />,
      to: ADMIN_EXERCISE_LEADERBOARD_PATH,
      groupIndex: 1,
    },
    {
      label: "Map",
      icon: <IconMap {...iconProps} />,
      to: ADMIN_MAP_PATH,
      groupIndex: 2,
    },
    {
      label: "Simulator Fleet",
      to: "/admin/simulator-fleet",
      icon: <IconGrid4x4 {...iconProps} />,
      groupIndex: 2,
    },
    {
      label: "Client distribution",
      to: "/admin/client-distribution",
      icon: <IconBuildingStore {...iconProps} />,
      groupIndex: 2,
    },
    {
      label: "License history",
      to: "/admin/license-updates",
      icon: <IconHistoryToggle {...iconProps} />,
      groupIndex: 2,
    },
    {
      label: "Partners",
      to: "/admin/partners",
      icon: <IconHeartHandshake {...iconProps} />,
      groupIndex: 3,
    },
    {
      label: "Custom courses",
      icon: <IconEdit {...iconProps} />,
      to: ADMIN_COURSE_EDITOR_STATS_PATH,
      groupIndex: 3,
    },
    {
      label: "Teacher training",
      icon: <IconSchool {...iconProps} />,
      to: `${ADMIN_ACADEMY_PATH}`,
      groupIndex: 3,
    },
    {
      label: "Competitions",
      icon: <IconAward {...iconProps} />,
      to: ADMIN_COMPETITIONS_PATH,
      groupIndex: 3,
    },
    {
      label: "Top list",
      icon: <IconTrophy {...iconProps} />,
      to: ADMIN_TOPLIST_PATH,
      groupIndex: 3,
    },
  ];

  if (hasBillingPermission)
    items.splice(0, 0, {
      label: "Clients",
      icon: <IconFileInvoice {...iconProps} />,
      to: "/admin/clients",
      groupIndex: 0,
    });

  return items;
};

function AdminMenu() {
  const adminMenuItems = useAdminMenuItems();
  return (
    <Menu.Sub.Dropdown style={{ overflowY: "auto" }}>
      {adminMenuItems.map((i) => {
        return (
          <Menu.Item
            key={i.label}
            leftSection={i.icon}
            component={Link}
            to={i.to}
          >
            {i.label}
          </Menu.Item>
        );
      })}
    </Menu.Sub.Dropdown>
  );
}

export default AdminMenu;
